import { Messages } from 'primereact/messages';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage';
import { getErrorMessage } from '../../../utils/errors';
import { ErrorMessage } from '../../components/ErrorMessage';
import { IconTooltip } from '../../components/IconTooltip';
import { getApiClient } from '../../../api';
import { formatDate } from '../../utils/format';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { scrollTop } from '../../utils/scroll';
import { MeetingEditForm } from './components/MeetingEditForm';
import { MeetingEditMemberTable } from './components/MeetingEditMemberTable';
import { MeetingBreadCrumb } from './components/MeetingBreadCrumb';
import { LineDivider } from '../../components/LineDivider';
import { ChangeMembershipDialog } from './components/ChangeMembershipDialog';
import { ConfirmRemoveMemberDialog } from './components/ConfirmRemoveMemberDialog';
import { DateOverlapDialog } from './components/DateOverlapDialog';
import { ButtonAdd } from '../../components/Buttons';
import { MeetingEditAdditionalMembersDialog } from './components/MeetingEditAdditionalMembersDialog';
import { Toast } from 'primereact/toast';

export default function MeetingEdit() {
    const { municipalityId, bodyType, termId, meetingId } = useParams();

    const msgs = useRef(null);
    const toast = useRef(null);
    const currentLocale = 'cs';

    const [loading, setLoading] = useState(true);
    const [initError, setInitError] = useState(false);
    const [meeting, setMeeting] = useState(null);
    const [politicalEntities, setPoliticalEntities] = useState([]);

    const [reloading, setReloading] = useState(false);

    const [selectedMemberRow, setSelectedMemberRow] = useState(null);

    const [attendanceOptionsList, setAttendanceOptionsList] = useState([]);

    const [isVisibleChangeMembershipDialog, setIsVisibleChangeMembershipDialog] = useState(false);
    const [processingChangeMembership, setProcessingChangeMembership] = useState(false);
    const refChangeMembershipDialogMsgs = useRef(null);

    const [isVisibleConfirmRemoveMemberDialog, setIsVisibleConfirmRemoveMemberDialog] = useState(false);
    const [processingRemoveMember, setProcessingRemoveMember] = useState(false);
    const refConfirmRemoveMemberDialogMsgs = useRef(null);

    const [isVisibleAddMembersDialog, setIsVisibleAddMembersDialog] = useState(false);
    const [processingAddMembers, setProcessingAddMembers] = useState(false);
    const refAddMembersDialogMsgs = useRef(null);

    const [pendingFormData, setPendingFormData] = useState(null);
    const [overlaps, setOverlaps] = useState(null);
    const [isVisibleDateOverlapDialog, setIsVisibleOverlapDialog] = useState(false);
    const refDateOverlapDialogMsgs = useRef(null);

    const [processingChangeAttendance, setProcessingChangeAttendance] = useState(false);

    const disabled = useMemo(
        () =>
            processingChangeMembership || processingRemoveMember || processingAddMembers || processingChangeAttendance,
        [processingChangeMembership, processingRemoveMember, processingAddMembers, processingChangeAttendance]
    );

    const fetchMeeting = useCallback(async () => {
        return getApiClient().getAdminMeetingByMunicipalityIdAndBodyAndMeetingId(municipalityId, bodyType, Number(meetingId), { include_available: true });
    }, [municipalityId, bodyType, meetingId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [meetingData, politicalEntitiesData, attendanceOptionsResponse] = await Promise.all([
                    fetchMeeting(),
                    getApiClient().getAdminPoliticalEntityByMunicipalityIdAndBody(municipalityId, bodyType),
                    getApiClient().getAttendanceOptionsLanguageListByLang(currentLocale)
                ]);
                setMeeting(meetingData);
                setPoliticalEntities(politicalEntitiesData);
                setAttendanceOptionsList(attendanceOptionsResponse);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setInitError(errorMessage ?? 'Nepodařilo se načíst zvolené zasedání');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentLocale, municipalityId, fetchMeeting]);

    const reloadMeeting = async () => {
        setReloading(true);
        try {
            const meetingResponse = await fetchMeeting();
            setMeeting(meetingResponse);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se načíst nová data zasedání', { replace: true });
            scrollTop();
        } finally {
            setReloading(false);
        }
    };

    const forceSubmit = async (data) => {
        await onSubmit({ ...data, _force: true });
    };

    const onSubmit = async (data) => {
        const force = data._force || false;

        const requestData = {
            date_from: formatDate(data.date_from),
            date_to: data.date_from.getTime() === data.date_to?.getTime() ? null : formatDate(data.date_to),
            number: data.number,
            recording_url: data.recording_url,
            force
        };

        try {
            await getApiClient().putAdminMeetingByMunicipalityIdAndBodyAndMeetingId(municipalityId, bodyType, Number(meetingId), requestData);
            showSuccessMessage(msgs, 'Zasedání bylo úspěšně upraveno');
            scrollTop();
        } catch (error) {
            if (error.response?.status === 409) {
                setPendingFormData(data);
                showDateOverlapDialog(error.response?.data);
            } else {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se přidat nové zasedání', { replace: true });
                scrollTop();
            }
        }
    };

    const showChangeMembershipDialog = (memberRow) => {
        setSelectedMemberRow(memberRow);
        setIsVisibleChangeMembershipDialog(true);
    };

    const hideChangeMembershipDialog = () => {
        setIsVisibleChangeMembershipDialog(false);
        setSelectedMemberRow(null);
    };

    const handleChangeMembership = async (data) => {
        if (!selectedMemberRow) {
            hideChangeMembershipDialog();
            return;
        }

        setProcessingChangeMembership(true);

        const requestData = {
            political_entity_id: data.political_entity
        };
        try {
            await getApiClient().patchAdminPoliticalEntityByMunicipalityIdAndBodyAndMeetingIdAndMemberId(municipalityId, bodyType, Number(meetingId), selectedMemberRow.id, requestData);
            showSuccessMessage(msgs, 'Členství bylo úspěšně změněno');
            reloadMeeting();
            hideChangeMembershipDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refChangeMembershipDialogMsgs, errorMessage ?? 'Nepodařilo se změnit členství', {
                replace: true
            });
        } finally {
            setProcessingChangeMembership(false);
        }
    };

    const showConfirmRemoveMemberDialog = (memberRow) => {
        setSelectedMemberRow(memberRow);
        setIsVisibleConfirmRemoveMemberDialog(true);
    };

    const hideConfirmRemoveMemberDialog = () => {
        setIsVisibleConfirmRemoveMemberDialog(false);
        setSelectedMemberRow(null);
    };

    const handleRemoveMember = async () => {
        if (!selectedMemberRow) {
            hideConfirmRemoveMemberDialog();
            return;
        }

        setProcessingRemoveMember(true);

        try {
            await getApiClient().deleteAdminMemberByMunicipalityIdAndBodyAndMeetingIdAndMemberId(municipalityId, bodyType, Number(meetingId), selectedMemberRow.id);
            showSuccessMessage(
                msgs,
                selectedMemberRow.sex ? 'Členka byla úspěšně odebrána' : 'Člen byl úspěšně odebrán'
            );
            reloadMeeting();
            hideConfirmRemoveMemberDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refConfirmRemoveMemberDialogMsgs,
                errorMessage ?? `Nepodařilo se odebrat ${selectedMemberRow.sex ? 'členku' : 'člena'}`,
                {
                    replace: true
                }
            );
        } finally {
            setProcessingRemoveMember(false);
        }
    };

    const showAddMembersDialog = () => {
        setIsVisibleAddMembersDialog(true);
    };

    const hideAddMembersDialog = () => {
        setIsVisibleAddMembersDialog(false);
    };

    const handleAddMembers = async (data) => {
        if (!data.members.length) {
            hideAddMembersDialog();
            return;
        }

        const requestData = {
            members: data.members.map((member) => ({
                id: member.member_id,
                political_entity: member.political_entity,
                attendance: member.attendance
            }))
        };

        setProcessingAddMembers(true);

        try {
            await getApiClient().postAdminMemberByMunicipalityIdAndBodyAndMeetingId(municipalityId, bodyType, Number(meetingId), requestData);
            showSuccessMessage(
                msgs,
                requestData.members.length > 1 ? 'Členové byli úspěšně přidání' : 'Člen byl úspěšně přidán'
            );
            reloadMeeting();
            hideAddMembersDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refAddMembersDialogMsgs,
                errorMessage ?? `Nepodařilo se přidat ${requestData.members.length > 1 ? 'člena' : 'členy'}`,
                {
                    replace: true
                }
            );
        } finally {
            setProcessingAddMembers(false);
        }
    };

    const showDateOverlapDialog = async (data) => {
        setOverlaps(data.overlaps);
        setIsVisibleOverlapDialog(true);
    }

    const hideDateOverlapDialog = () => {
        setOverlaps(null);
        setIsVisibleOverlapDialog(false);
    };

    const handleDateOverlap = async () => {
        if (!pendingFormData) return;

        forceSubmit(pendingFormData);
        setPendingFormData(null);
        hideDateOverlapDialog();
    }

    const handleAttendanceChange = async (e, memberId) => {
        setProcessingChangeAttendance(true);

        const requestData = {
            attendance: e.value
        };
        try {
            await getApiClient().patchAdminAttendanceByMunicipalityIdAndBodyAndMeetingIdAndMemberId(municipalityId, bodyType, Number(meetingId), memberId, requestData);
            toast?.current?.replace({
                severity: 'success',
                summary: 'Přítomnost byla úspěšně změněna'
            });
            const newMembers = meeting.members.map((member) => {
                if (member.id === memberId) {
                    return {
                        ...member,
                        attendance: e.value
                    };
                }
                return member;
            });
            setMeeting({
                ...meeting,
                members: newMembers
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            toast?.current?.replace({
                severity: 'error',
                summary: errorMessage ?? 'Nepodařilo se změnit přítomnost'
            });
        } finally {
            setProcessingChangeAttendance(false);
        }
    };

    return loading ? (
        <LoadingPage />
    ) : initError ? (
        <>
            <MeetingBreadCrumb />
            <h1>Upravit zasedání</h1>
            <ErrorMessage text={initError} />
        </>
    ) : (
        <>
            <Toast position='top-center' ref={toast} className='toast-container' />

            <MeetingBreadCrumb
                extraItems={[
                    {
                        label: 'Upravit',
                        url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani/${meetingId}`
                    }
                ]}
            />
            <h1>Upravit zasedání č. {meeting.number}</h1>

            <Messages ref={msgs} className='page__messages' />

            <MeetingEditForm onSubmit={onSubmit} meeting={meeting} disabled={disabled} />
            <LineDivider />
            <div className='flex-vertical-center'>
                <h2>
                    Členové zasedání
                    <IconTooltip>
                        <>
                            <p>Členové, kteří měli aktivní členství v době konání zasedání (včetně nepřítomných).</p>
                            <p>
                                Pro řazení podle více sloupců přidržte klávesu <i>Ctrl</i> při výběru dalšího sloupce.
                            </p>
                        </>
                    </IconTooltip>
                    <span title='Počet členů' className='ml-2'>
                        ({meeting.members.length})
                    </span>
                </h2>
                <ButtonAdd
                    label='Přidat členy'
                    className='ml-2'
                    icon='bi bi-person-plus'
                    onClick={showAddMembersDialog}
                    disabled={disabled}
                />
            </div>

            <MeetingEditMemberTable
                members={meeting.members}
                disabled={disabled}
                attendanceOptionsList={attendanceOptionsList}
                onClickChangePoliticalEntity={showChangeMembershipDialog}
                onClickRemoveMember={showConfirmRemoveMemberDialog}
                onAttendanceChange={handleAttendanceChange}
            />

            <ChangeMembershipDialog
                isVisible={isVisibleChangeMembershipDialog}
                politicalEntities={politicalEntities}
                selectedMember={selectedMemberRow}
                onHide={hideChangeMembershipDialog}
                onSubmit={handleChangeMembership}
                msgs={refChangeMembershipDialogMsgs}
            />

            <ConfirmRemoveMemberDialog
                isVisible={isVisibleConfirmRemoveMemberDialog}
                selectedMember={selectedMemberRow}
                onHide={hideConfirmRemoveMemberDialog}
                onClickDelete={handleRemoveMember}
                msgs={refConfirmRemoveMemberDialogMsgs}
            />

            <MeetingEditAdditionalMembersDialog
                isVisible={isVisibleAddMembersDialog}
                members={meeting.available_members}
                attendanceOptionsList={attendanceOptionsList}
                loading={reloading}
                politicalEntities={politicalEntities}
                onHide={hideAddMembersDialog}
                onSubmit={handleAddMembers}
                msgs={refAddMembersDialogMsgs}
            />

            <DateOverlapDialog
                overlaps={overlaps}
                isVisible={isVisibleDateOverlapDialog}
                onHide={hideDateOverlapDialog}
                onClickConfirm={handleDateOverlap}
                msgs={refDateOverlapDialogMsgs}
            />
        </>
    );
}
